// src/admin/components/products/ProductList.tsx
import React from 'react';
import {
  Edit2,
  Trash2,
  Eye,
  ArrowUpRight,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Product } from '../../../types';

interface ProductListProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (productId: string | number) => void;
  onToggleStatus: (productId: string | number, active: boolean) => void;
}

// Helper: if url doesn't start with http:// or https://, prepend our backend URL from environment
const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;
function getFullImageUrl(url: string | undefined): string {
  if (!url) return 'https://via.placeholder.com/80?text=No+Image';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return backendBaseImagesURL + url; // e.g. "/uploads/images/filename" => "http://localhost:5000/uploads/images/filename"
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  onEdit,
  onDelete,
  onToggleStatus
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active
              </th>
              <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {products.map((product) => {
              // Pick a main image if one exists
              let mainImage = 'https://via.placeholder.com/80?text=No+Image';
              if (product.images && product.images.length > 0) {
                mainImage = getFullImageUrl(product.images[0].url);
              } else if (product.image) {
                // If there's an older "product.image" field, also convert it
                mainImage = getFullImageUrl(product.image);
              }

              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <img
                        src={mainImage}
                        alt={product.name}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          SKU: {product.sku || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {product.category_name ||
                      product.category_id ||
                      'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    KES {product.price?.toLocaleString() || '0'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {product.stock ?? 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-xs">
                      {product.featured ? (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-600">
                          Featured
                        </span>
                      ) : null}
                      {product.best_seller ? (
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-600">
                          Best Seller
                        </span>
                      ) : null}
                      {product.new_arrival ? (
                        <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-600">
                          New
                        </span>
                      ) : null}
                      {product.most_viewed ? (
                        <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-600">
                          Most Viewed
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => onToggleStatus(product.id, !product.active)}
                      className="text-gray-600 hover:text-pink-500"
                    >
                      {product.active ? (
                        <ToggleRight className="h-6 w-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-red-500" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit(product)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(product.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button className="p-1 text-gray-600 hover:bg-gray-50 rounded">
                        <Eye className="h-4 w-4" />
                      </button>
                      <a
                        href={`/product/${product.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
